import { Component } from '@theme/component';
import { swapPanels, expandContainer } from '@theme/tab-panels';

class SupportProductsComponent extends Component {
  #triggers = [];
  #panels = [];
  #activeGroup = '';
  #overflowToggles = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#triggers = Array.from(this.querySelectorAll('[data-sp-trigger]'));
    this.#panels = Array.from(this.querySelectorAll('[data-sp-panel]'));
    this.#activeGroup = this.dataset.spInitial || this.#triggers[0]?.dataset.spGroup || '';

    if (!this.#triggers.length || !this.#panels.length) return;

    for (const trigger of this.#triggers) {
      trigger.addEventListener('click', this.#handleTriggerClick);
    }

    this.#syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const trigger of this.#triggers) {
      trigger.removeEventListener('click', this.#handleTriggerClick);
    }

    for (const [toggle, handler] of this.#overflowToggles) {
      toggle.removeEventListener('click', handler);
    }
    this.#overflowToggles.clear();
  }

  #handleTriggerClick = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const nextGroup = trigger.dataset.spGroup;
    if (!nextGroup || nextGroup === this.#activeGroup) return;

    this.#activeGroup = nextGroup;
    this.#syncState();
  };

  #syncState() {
    for (const trigger of this.#triggers) {
      const isActive = trigger.dataset.spGroup === this.#activeGroup;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    let activePanel = null;
    let previousVisible = null;

    for (const panel of this.#panels) {
      const isActive = panel.dataset.spGroup === this.#activeGroup;

      if (isActive) {
        activePanel = panel;
      } else if (!panel.hidden) {
        previousVisible = panel;
      }

      if (!isActive) {
        this.#collapseOverflow(panel);
      }
    }

    if (!activePanel) return;

    this.#mountPanelTemplate(activePanel);
    this.#bindViewAll(activePanel);
    this.#collapseOverflow(activePanel);

    swapPanels({ container: this, oldPanel: previousVisible, newPanel: activePanel });
  }

  #mountPanelTemplate(panel) {
    if (panel.dataset.spMounted === 'true') return;

    const template = panel.querySelector('[data-sp-template]');
    if (!template) {
      panel.dataset.spMounted = 'true';
      return;
    }

    panel.appendChild(template.content.cloneNode(true));
    template.remove();
    panel.dataset.spMounted = 'true';
  }

  #bindViewAll(panel) {
    const toggle = panel.querySelector('[data-sp-more]');
    if (!toggle || this.#overflowToggles.has(toggle)) return;

    const handler = () => {
      this.#expandOverflow(panel);
    };

    toggle.addEventListener('click', handler);
    this.#overflowToggles.set(toggle, handler);
  }

  #collapseOverflow(panel) {
    const overflow = panel.querySelector('[data-sp-overflow]');
    const toggle = panel.querySelector('[data-sp-more]');

    if (overflow) overflow.hidden = true;
    if (toggle) {
      toggle.hidden = !overflow;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.spMoreLabel || 'View all';
    }
  }

  #expandOverflow(panel) {
    const overflow = panel.querySelector('[data-sp-overflow]');
    const toggle = panel.querySelector('[data-sp-more]');

    // Height must be measured BEFORE the overflow content is revealed.
    const fromHeight = this.offsetHeight;

    if (overflow) overflow.hidden = false;
    if (toggle) {
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'true');
    }

    expandContainer(this, fromHeight, panel);
  }
}

if (!customElements.get('support-products-component')) {
  customElements.define('support-products-component', SupportProductsComponent);
}
